import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, Phone, Calendar, Users, Send, CheckCircle2, DollarSign, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { adminApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const fmtMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';
const fmtDateTime = (s) => s ? new Date(s).toLocaleString() : '—';

const StatusPill = ({ status }) => {
  const map = {
    active: { label: 'Active', cls: 'border-[#4b5320]/40 bg-[#4b5320]/10 text-[#4b5320]' },
    trialing: { label: 'Trialing', cls: 'border-blue-300 bg-blue-50 text-blue-700' },
    past_due: { label: 'Past Due', cls: 'border-amber-300 bg-amber-50 text-amber-700' },
    canceled: { label: 'Canceled', cls: 'border-stone-300 bg-stone-100 text-stone-600' },
    pending: { label: 'Pending', cls: 'border-stone-300 bg-stone-100 text-stone-500' },
    review: { label: 'Review', cls: 'border-[#4b5320]/40 bg-[#4b5320]/10 text-[#4b5320]' },
    completed: { label: 'Complete', cls: 'border-stone-300 bg-stone-100 text-stone-600' },
  };
  const s = map[status] || { label: status || 'No sub', cls: 'border-stone-300 bg-stone-100 text-stone-500' };
  return <span className={`inline-block text-xs uppercase tracking-widest px-2 py-0.5 rounded-sm border ${s.cls}`}>{s.label}</span>;
};

const AdminCompanyDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteCo, setConfirmDeleteCo] = useState(false);
  const [confirmDeleteClient, setConfirmDeleteClient] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await adminApi.companyDetail(userId);
      setData(r.data);
    } catch (e) {
      toast({ title: 'Could not load company', description: e.response?.data?.detail || 'Try again' });
      navigate('/app/admin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [userId]);

  const handleDeleteCompany = async () => {
    setBusy(true);
    try {
      await adminApi.deleteCompany(userId);
      toast({ title: 'Company deleted', description: `${data?.company?.company || data?.company?.email} and all data removed.` });
      navigate('/app/admin');
    } catch (e) {
      if (e.response?.status === 404) {
        toast({ title: 'Already deleted', description: 'This company was already removed. Going back to the list.' });
        navigate('/app/admin');
      } else {
        toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again.' });
        setBusy(false);
      }
    }
  };

  const handleDeleteClient = async () => {
    if (!confirmDeleteClient) return;
    setBusy(true);
    try {
      await adminApi.deleteClient(userId, confirmDeleteClient.id);
      toast({ title: 'Client deleted', description: `${confirmDeleteClient.name} removed.` });
      setConfirmDeleteClient(null);
      await load();
    } catch (e) {
      if (e.response?.status === 404) {
        toast({ title: 'Already deleted', description: 'This client was already removed. Refreshing.' });
        setConfirmDeleteClient(null);
        await load();
      } else {
        toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again.' });
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-stone-500">Loading company...</div>;
  if (!data) return null;

  const { company, clients, disputes, activity, metrics } = data;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <Link to="/app/admin" className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Admin Console
      </Link>

      {/* Header */}
      <div className="border border-stone-200/80 bg-white rounded-sm p-8 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-[#4b5320]/15 border border-[#4b5320]/40 text-[#4b5320] flex items-center justify-center rounded-sm font-display text-2xl">
              {company.avatar || (company.company || company.name || 'C').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Company File</div>
              <h1 className="font-display text-3xl text-stone-900">{company.company || company.name}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-stone-600">
                <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {company.email}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Signed up {fmtDate(company.createdAt)}</span>
                <StatusPill status={company.subscriptionStatus} />
              </div>
              {company.stripeCustomerId && (
                <div className="text-xs text-stone-500 mt-1 font-mono">Stripe customer: {company.stripeCustomerId}</div>
              )}
            </div>
          </div>
          <Button onClick={() => setConfirmDeleteCo(true)} variant="outline" className="border-red-300 bg-white text-red-700 hover:bg-red-50 rounded-sm">
            <Trash2 className="w-4 h-4 mr-2" /> Delete Company
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-stone-200/80">
          <Stat label="Total Clients" value={metrics.totalClients} icon={Users} />
          <Stat label="Billable Clients" value={metrics.billableClients} icon={CheckCircle2} />
          <Stat label="Total Disputes" value={metrics.totalDisputes} icon={Send} />
          <Stat label="MRR Contribution" value={fmtMoney(metrics.mrrCents)} icon={DollarSign} highlight />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Clients */}
        <div className="lg:col-span-2 border border-stone-200/80 bg-white rounded-sm">
          <div className="p-6 border-b border-stone-200/80">
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Roster</div>
            <h2 className="font-display text-xl text-stone-900">Clients ({clients.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200/80 text-xs uppercase tracking-widest text-stone-500">
                  <th className="text-left px-5 py-3 font-medium">Client</th>
                  <th className="text-left px-3 py-3 font-medium">Status</th>
                  <th className="text-right px-3 py-3 font-medium">Items</th>
                  <th className="text-right px-3 py-3 font-medium">Disputes</th>
                  <th className="text-right px-3 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200/80">
                {clients.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-12 text-center text-stone-500">No clients enrolled.</td></tr>
                )}
                {clients.map(c => (
                  <tr key={c.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="text-stone-900 font-medium">{c.name}</div>
                      <div className="text-xs text-stone-500">{c.email}</div>
                    </td>
                    <td className="px-3 py-3"><StatusPill status={c.status} /></td>
                    <td className="px-3 py-3 text-right font-mono text-stone-800">
                      <span>{c.itemsRemoved}</span><span className="text-stone-400">/{c.negativeItems}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-stone-800">{c.totalDisputes}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setConfirmDeleteClient(c)}
                        className="inline-flex items-center text-stone-400 hover:text-red-600 transition-colors"
                        title="Delete client"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity */}
        <div className="border border-stone-200/80 bg-white rounded-sm">
          <div className="p-6 border-b border-stone-200/80">
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Field Report</div>
            <h2 className="font-display text-xl text-stone-900">Activity</h2>
          </div>
          <div className="divide-y divide-stone-200/80 max-h-[500px] overflow-y-auto">
            {activity.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">No activity.</div>}
            {activity.map(a => (
              <div key={a.id} className="px-5 py-3">
                <div className="text-sm text-stone-800">{a.message}</div>
                <div className="text-xs text-stone-500 mt-1">{a.clientName}</div>
                <div className="text-xs text-stone-400 mt-0.5">{fmtDateTime(a.createdAt)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent disputes */}
      <div className="border border-stone-200/80 bg-white rounded-sm mt-6">
        <div className="p-6 border-b border-stone-200/80">
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Operations</div>
          <h2 className="font-display text-xl text-stone-900">Recent Disputes ({disputes.length})</h2>
        </div>
        <div className="divide-y divide-stone-200/80">
          {disputes.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">No disputes yet.</div>}
          {disputes.map(d => (
            <div key={d.id} className="px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-stone-900 font-medium">{d.clientName}</div>
                <div className="text-xs text-stone-500">{d.type} · {d.bureau} · Round {d.round} · Sent {d.dateSent}</div>
              </div>
              <StatusPill status={d.status === 'awaiting_response' ? 'pending' : d.status} />
            </div>
          ))}
        </div>
      </div>

      {/* Delete company dialog */}
      <Dialog open={confirmDeleteCo} onOpenChange={setConfirmDeleteCo}>
        <DialogContent className="bg-white border-stone-200 text-stone-900 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-red-700">Delete Company</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-stone-700 space-y-3">
            <p>Permanently delete <strong>{company.company || company.email}</strong>?</p>
            <div className="bg-stone-100 border border-stone-200 rounded-sm p-3 text-xs space-y-1">
              <div>• {metrics.totalClients} clients</div>
              <div>• {metrics.totalDisputes} disputes</div>
              <div>• All uploaded documents</div>
              <div>• Active Stripe subscription will be canceled immediately</div>
            </div>
            <div className="flex items-start gap-2 text-red-700 text-xs font-semibold">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>This cannot be undone.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteCo(false)} className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">Cancel</Button>
            <Button onClick={handleDeleteCompany} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-sm">
              <Trash2 className="w-4 h-4 mr-2" /> {busy ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete client dialog */}
      <Dialog open={!!confirmDeleteClient} onOpenChange={(o) => !o && setConfirmDeleteClient(null)}>
        <DialogContent className="bg-white border-stone-200 text-stone-900 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-red-700">Delete Client</DialogTitle>
          </DialogHeader>
          {confirmDeleteClient && (
            <div className="text-sm text-stone-700 space-y-3">
              <p>Permanently delete <strong>{confirmDeleteClient.name}</strong>?</p>
              <div className="bg-stone-100 border border-stone-200 rounded-sm p-3 text-xs space-y-1">
                <div>• {confirmDeleteClient.documents} documents</div>
                <div>• {confirmDeleteClient.negativeItems} negative items</div>
                <div>• {confirmDeleteClient.totalDisputes} disputes</div>
                {confirmDeleteClient.totalDisputes > 0 && (
                  <div className="text-amber-700 font-semibold mt-2">• Company's Stripe subscription quantity will decrement</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteClient(null)} className="border-stone-300 bg-white text-stone-700 hover:bg-stone-100 rounded-sm">Cancel</Button>
            <Button onClick={handleDeleteClient} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-sm">
              <Trash2 className="w-4 h-4 mr-2" /> {busy ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, highlight }) => (
  <div className="border-l-2 pl-4" style={{ borderColor: highlight ? '#4b5320' : '#d6d3d1' }}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 mb-1">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className={`font-display text-3xl ${highlight ? 'text-[#4b5320]' : 'text-stone-900'}`}>{value}</div>
  </div>
);

export default AdminCompanyDetail;
